import {CSSProperties, memo, useMemo} from "react";
import {CELL_SIZE, ClientDiagramConnection, ClientDiagramNode} from "../../utility/types";
import {getConnectionWaypoints, generateConnectionPath} from "../../utility/connectionPath";
import {Vec2} from "../../utility/math/vec2";
import {DiagramNodeId} from "platformed-client/types";
import {ColorPalette, useResolvedColorPalettes} from "../../utility/colors";

const useConnectionPath = (waypoints: Vec2[]) => {
  return useMemo(() => {
    const gridPath = generateConnectionPath(waypoints);
    return gridPath.map(gridPos => gridPos.mulScalar(CELL_SIZE).addScalar(0.5 * CELL_SIZE));
  }, [waypoints]);
};

const svgStyle: CSSProperties = {position: "absolute", top: 0, left: 0, overflow: "visible"};

export const BaseConnectionLine = memo(({waypoints, colorPalette}: {waypoints: Vec2[]; colorPalette: ColorPalette}) => {
  const colorPalettes = useResolvedColorPalettes();
  const pixelPath = useConnectionPath(waypoints);

  const svgPath = useMemo(() => {
    if (pixelPath.length < 2) return "";

    let path = `M ${pixelPath[0].x} ${pixelPath[0].y}`;
    for (let i = 1; i < pixelPath.length; i++) {
      path += ` L ${pixelPath[i].x} ${pixelPath[i].y}`;
    }
    return path;
  }, [pixelPath]);

  if (!svgPath) return null;

  return (
    <svg style={svgStyle}>
      <path
        d={svgPath}
        stroke={colorPalettes[colorPalette]}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
});

export const BaseConnectionArrowHead = memo(
  ({waypoints, anchorPositions = []}: {waypoints: Vec2[]; anchorPositions?: Vec2[]}) => {
    const pixelPath = useConnectionPath(waypoints);

    const arrowTransform = useMemo(() => {
      if (pixelPath.length < 3) return null;

      // Convert anchor positions to pixel coordinates for comparison
      const pixelAnchors = anchorPositions.map(anchor => anchor.mulScalar(CELL_SIZE).addScalar(0.5 * CELL_SIZE));

      // Search backwards from the 2nd-to-last position to find a suitable arrow position
      // that doesn't overlap with an anchor point
      let arrowIndex = pixelPath.length - 2;
      for (let i = pixelPath.length - 2; i >= 1; --i) {
        const candidate = pixelPath[i];
        const hasAnchor = pixelAnchors.some(anchor => anchor.equals(candidate));

        if (!hasAnchor) {
          arrowIndex = i;
          break;
        }
      }

      const arrowPoint = pixelPath[arrowIndex];
      const nextPoint = pixelPath[arrowIndex + 1];

      // Calculate direction vector from arrow point to next point
      const direction = new Vec2(nextPoint.x - arrowPoint.x, nextPoint.y - arrowPoint.y);

      // Calculate rotation angle in degrees
      const rotation = Math.atan2(direction.y, direction.x) * (180 / Math.PI);

      return {x: arrowPoint.x, y: arrowPoint.y, rotation};
    }, [pixelPath, anchorPositions]);

    if (!arrowTransform) return null;

    return (
      <svg style={svgStyle}>
        <polygon
          points="-8,-8 8,0 -8,8"
          fill="black"
          stroke="white"
          strokeWidth="1.5"
          transform={`translate(${arrowTransform.x}, ${arrowTransform.y}) rotate(${arrowTransform.rotation})`}
        />
      </svg>
    );
  },
);

export const BaseConnectionArrow = memo(
  ({waypoints, colorPalette}: {waypoints: Vec2[]; colorPalette: ColorPalette}) => {
    return (
      <>
        <BaseConnectionLine waypoints={waypoints} colorPalette={colorPalette} />
        <BaseConnectionArrowHead waypoints={waypoints} />
      </>
    );
  },
);

export interface ConnectionArrowProps {
  connection: ClientDiagramConnection;
  nodes: ReadonlyMap<DiagramNodeId, ClientDiagramNode>;
}

export const ConnectionLine = memo(({connection, nodes}: ConnectionArrowProps) => {
  const waypoints = useMemo(() => {
    return getConnectionWaypoints(connection, nodes);
  }, [connection, nodes]);

  return <BaseConnectionLine waypoints={waypoints} colorPalette={connection.color} />;
});

export const ConnectionArrowHead = memo(({connection, nodes}: ConnectionArrowProps) => {
  const waypoints = useMemo(() => {
    return getConnectionWaypoints(connection, nodes);
  }, [connection, nodes]);

  const anchorPositions = useMemo(() => {
    return connection.anchors.map(anchor => anchor.position);
  }, [connection.anchors]);

  return <BaseConnectionArrowHead waypoints={waypoints} anchorPositions={anchorPositions} />;
});
